/**
 * BrandbookAgentCard — the "create one instead of uploading one" entrance.
 *
 * The panels further down CONSUME a design-skill bundle: someone made a .zip
 * elsewhere and uploads it there. This card is the other half — it hands the
 * admin off to an agent that BUILDS the bundle from the company's brand book,
 * logos and fonts, and gives back a .zip to try and then upload.
 *
 * It deliberately lists the real agents rather than offering a generic
 * "Create with AI" button that guesses one: a button that navigates into a
 * conversation with an agent that has no brand-book tools is exactly the
 * dead end this card exists to prevent. Brand-book authoring is the Brand
 * Book Studio's alone (operator decision 2026-09-27): this card is the only
 * way a studio comes to exist (`ensureBrandbookStudio`), the agent editor
 * has no toggle for it, and the studio is marked
 * (`chatbot_config.brandbook_studio`) by the service that creates it.
 *
 * With several, one is primary and the rest sit in a picker beside it: a row
 * of equally-weighted buttons made a throwaway ("Test Cross-Tenant Bot 3")
 * look like a considered choice next to the real studio agent.
 */
import { useEffect, useState } from 'react';
import { Button, Select, Spin, message as antdMessage } from 'antd';
import { RobotOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/hooks/useLanguage';
import { useTenantPath } from '@/routing';
import botService from '@modules/chatbots/services/botService';
import { ensureBrandbookStudio } from '@/modules/super_admin/design_system/services/designSkillService';
import { useAuth } from '@/hooks/useAuth';
import type { Bot } from '@/types/models';

export default function BrandbookAgentCard() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const tenantPath = useTenantPath();
  const { tenantId } = useAuth();
  const [agents, setAgents] = useState<Bot[] | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Ask the server for the authoring agents rather than filtering a page
    // of all of them: the list is paginated, and on a tenant with more
    // agents than one page the studio was simply absent from the response,
    // so this card decided there were none and offered no way in.
    botService.getBrandbookAuthoringBots()
      .then(bots => {
        if (cancelled) return;
        setAgents(bots);
        setChosen(bots[0]?.chatbot_id ?? null);
      })
      .catch(() => { if (!cancelled) setAgents([]); });
    return () => { cancelled = true; };
  }, []);

  // With no authoring agent this used to be a dead end: a sentence naming a
  // switch, and no way to get a brand book. The studio is provisioned on
  // demand instead, so the offer this card makes is always true. Idempotent
  // server-side, so a second press cannot make a second agent.
  const open = async () => {
    if (chosen) { navigate(tenantPath(`/chat?bot=${chosen}`)); return; }
    if (!tenantId) return;
    setPreparing(true);
    try {
      const studio = await ensureBrandbookStudio(tenantId);
      navigate(tenantPath(`/chat?bot=${studio.chatbot_id}`));
    } catch {
      antdMessage.error(t('systemDesign.brandbookAgent.couldNotPrepare'));
    } finally {
      setPreparing(false);
    }
  };

  return (
    <section className="wl-section" data-testid="brandbook-agent-card">
      <header className="wl-section-header">
        <h4 className="wl-section-title">
          <RobotOutlined className="wl-section-icon" /> {t('systemDesign.brandbookAgent.title')}
        </h4>
        <p className="wl-section-subtitle">{t('systemDesign.brandbookAgent.subtitle')}</p>
      </header>
      <div className="wl-section-body">
        {agents === null ? (
          // The list is in flight. The card stays mounted and only its body
          // waits: an early `return null` here made the whole section appear
          // several seconds after the page did, shoving the upload panels
          // down at the moment an admin was reaching for them.
          <Spin size="small" data-testid="brandbook-agent-loading" />
        ) : agents.length === 0 ? (
          <div className="wl-test-actions">
            <Button type="primary" loading={preparing} onClick={open}
                    data-testid="brandbook-agent-create">
              {t('systemDesign.brandbookAgent.start')}
            </Button>
            <span className="wl-section-why">{t('systemDesign.brandbookAgent.willCreate')}</span>
          </div>
        ) : (
          <div className="wl-test-actions">
            <Button type="primary" onClick={open} disabled={!chosen}
                    data-testid={`brandbook-agent-open-${chosen ?? 'none'}`}>
              {t('systemDesign.brandbookAgent.start')}
            </Button>
            {agents.length === 1 && (
              <span className="wl-section-why" data-testid="brandbook-agent-existing">
                {t('systemDesign.brandbookAgent.existing', { name: agents[0].chatbot_name })}
              </span>
            )}
            {agents.length > 1 && (
              <Select
                style={{ minWidth: 220 }}
                value={chosen}
                onChange={setChosen}
                options={agents.map(a => ({ value: a.chatbot_id, label: a.chatbot_name }))}
                data-testid="brandbook-agent-picker"
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
}
